import { computed, onMounted, onUnmounted, watch, reactive, shallowRef } from 'vue';
import { useStore } from 'vuex';
import { useCallControlSettings } from './useCallControlSettings';
import voipUnitsAPI from 'dashboard/api/voipUnits';

/**
 * Composable para controle de chamadas telefônicas via Asterisk Bridge
 * Gerencia conexão WebSocket, estados de chamada e ações de controle
 *
 * SINGLETON: Garante que apenas uma instância do WebSocket exista,
 * não importa quantos componentes usem este composable.
 */

// Variáveis no escopo do módulo para compartilhar entre todas as instâncias
let ringtoneAudio = null;
let wsInstance = null;
let reconnectTimer = null;
let answerTimeout = null;
let componentCount = 0;
let sipHangupFn = null;

let reconnectAttempts = 0;
let maxReconnectAttempts = 10;
let isIntentionalDisconnect = false;

// Handlers de rede e visibilidade — singleton para não registrar múltiplas vezes
let networkOnlineHandler = null;
let networkOfflineHandler = null;
let visibilityChangeHandler = null;

const LOCAL_STORAGE_KEY = 'callControlState';

// Unidades VoIP cadastradas no Super Admin (GET /voip/units), casadas pelo inboxId
// que o bridge manda em todo call_incoming. Carregado uma vez por sessão; se o
// endpoint falhar, os mapeamentos por nome (abaixo e no PhoneWidget) continuam valendo.
const voipUnits = shallowRef(null);
let voipUnitsPromise = null;

const loadVoipUnits = () => {
  if (voipUnitsPromise) return voipUnitsPromise;
  voipUnitsPromise = voipUnitsAPI
    .get()
    .then(({ data }) => {
      voipUnits.value = data?.units || [];
    })
    .catch(err => {
      console.warn('[CallControl] /voip/units indisponível — usando mapeamento por nome:', err?.message);
      voipUnitsPromise = null; // permite tentar de novo na próxima conexão
    });
  return voipUnitsPromise;
};

export const getVoipUnitByInboxId = inboxId => {
  if (!voipUnits.value || inboxId === null || inboxId === undefined) return null;
  return voipUnits.value.find(u => Number(u.inbox_id) === Number(inboxId)) || null;
};

const saveCallState = () => {
  try {
    if (sharedState.currentCall) {
      const state = {
        currentCall: sharedState.currentCall,
        activeCalls: sharedState.activeCalls,
        timestamp: Date.now(),
      };
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state));
      console.log('[CallControl] 💾 Estado da chamada salvo no localStorage');
    } else {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    }
  } catch (err) {
    console.error('[CallControl] Erro ao salvar estado no localStorage:', err);
  }
};

const loadCallState = () => {
  try {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (saved) {
      const state = JSON.parse(saved);
      const isRecent = (Date.now() - state.timestamp) < 5 * 60 * 1000;
      if (isRecent) {
        console.log('[CallControl] 📂 Estado da chamada restaurado do localStorage');
        return state;
      }
    }
  } catch (err) {
    console.error('[CallControl] Erro ao carregar estado do localStorage:', err);
  }
  return null;
};

const clearCallState = () => {
  try {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  } catch (err) {
    console.error('[CallControl] Erro ao limpar estado do localStorage:', err);
  }
};

// Estado compartilhado entre todas as instâncias (REATIVO)
const sharedState = reactive({
  isConnected: false,
  isConnecting: false,
  currentCall: null,
  isMuted: false,
  isOnHold: false,
  activeCalls: [],
  error: null,
  totalCallsToday: 0,
  totalDurationToday: 0,
  missedCallsToday: 0,
  currentSipExtension: null,
  inQueue: 0,
  isAnswering: false,
});

export function useCallControl() {
  const store = useStore();
  const { isEnabled } = useCallControlSettings();

  const isConnected = computed(() => sharedState.isConnected);
  const isConnecting = computed(() => sharedState.isConnecting);
  const currentCall = computed(() => sharedState.currentCall);
  const isMuted = computed(() => sharedState.isMuted);
  const isOnHold = computed(() => sharedState.isOnHold);
  const activeCalls = computed(() => sharedState.activeCalls);
  const error = computed(() => sharedState.error);
  const totalCallsToday = computed(() => sharedState.totalCallsToday);
  const totalDurationToday = computed(() => sharedState.totalDurationToday);
  const missedCallsToday = computed(() => sharedState.missedCallsToday);
  const inQueue = computed(() => sharedState.inQueue);
  const isAnswering = computed(() => sharedState.isAnswering);

  // Limpa o timeout de segurança do atendimento e o estado "conectando".
  // Chamado quando a chamada resolve (atendida/encerrada) por qualquer via.
  const clearAnswerTimeout = () => {
    if (answerTimeout) {
      clearTimeout(answerTimeout);
      answerTimeout = null;
    }
    sharedState.isAnswering = false;
  };

  const WS_URL = import.meta.env.VITE_ASTERISK_WS_URL || 'wss://asterisk-ctrl.lhgmoteis.com.br';

  const connect = () => {
    if (wsInstance && (wsInstance.readyState === WebSocket.OPEN || wsInstance.readyState === WebSocket.CONNECTING)) {
      return;
    }

    if (isConnecting.value) {
      return;
    }

    sharedState.isConnecting = true;
    sharedState.error = null;
    loadVoipUnits();

    try {
      let heartbeatTimer = null;
      const ws = new WebSocket(WS_URL);
      wsInstance = ws;

      ws.onopen = () => {
        if (wsInstance !== ws) return;

        reconnectAttempts = 0;
        sharedState.isConnected = true;
        sharedState.isConnecting = false;

        const currentUser = store.getters.getCurrentUser;
        const callControlStatus = isEnabled.value;
        const userSipExtension = `SIP/${999 + (currentUser?.id || 1)}`;

        const savedState = loadCallState();
        const activeCallId = savedState?.currentCall?.id || null;

        // Token de acesso da API do usuário logado (vem do /api/v1/profile via store de auth).
        // Vai no corpo da mensagem, nunca na URL do WebSocket: o Traefik grava a URL no access log.
        const accessToken = currentUser?.access_token;

        const authMessage = {
          type: 'auth',
          userId: currentUser.id,
          accountId: store.getters.getCurrentAccountId,
          callControlEnabled: callControlStatus,
          sipExtension: userSipExtension,
          activeCallId: activeCallId,
          ...(accessToken ? { token: accessToken } : {}),
        };
        ws.send(JSON.stringify(authMessage));

        // Restaurar estado visual — bridge responderá com call_ended se estiver stale
        if (savedState && savedState.currentCall) {
          console.log('[CallControl] 📞 Restaurando chamada ativa após reconexão:', savedState.currentCall.id);
          sharedState.currentCall = savedState.currentCall;
          sharedState.activeCalls = savedState.activeCalls;
        }

        // Heartbeat: mantém o WebSocket vivo em NATs com timeout curto
        heartbeatTimer = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 25000);
      };

      ws.onmessage = event => {
        const data = JSON.parse(event.data);
        handleMessage(data);
      };

      ws.onerror = err => {
        console.error('[CallControl] WebSocket error:', err);
        sharedState.error = 'Erro de conexão com o servidor de chamadas';
      };

      ws.onclose = () => {
        if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null; }
        sharedState.isConnected = false;
        sharedState.isConnecting = false;
        if (wsInstance === ws) wsInstance = null;

        if (isIntentionalDisconnect) {
          console.log('[CallControl] 📴 Desconexão intencional, não tentar reconectar');
          isIntentionalDisconnect = false;
          return;
        }

        if (!navigator.onLine) {
          console.log('[CallControl] 🌐 Rede offline, aguardando evento online para reconectar');
          return;
        }

        if (reconnectTimer) {
          clearTimeout(reconnectTimer);
          reconnectTimer = null;
        }

        const delay = Math.min(2000 * Math.pow(2, reconnectAttempts), 128000);
        reconnectAttempts++;

        console.log(`[CallControl] 🔄 WebSocket desconectado. Tentativa ${reconnectAttempts}/${maxReconnectAttempts}. Próxima reconexão em ${delay}ms`);

        if (reconnectAttempts <= maxReconnectAttempts) {
          const userAvailability = store.getters.getCurrentUserAvailability;
          if (userAvailability === 'online') {
            reconnectTimer = setTimeout(() => {
              connect();
            }, delay);
          }
        } else {
          console.error('[CallControl] ❌ Máximo de tentativas de reconexão atingido');
          sharedState.error = 'Não foi possível reconectar ao servidor de chamadas após várias tentativas';
          clearCallState();
        }
      };
    } catch (err) {
      console.error('[CallControl] Erro ao conectar:', err);
      sharedState.error = 'Erro ao conectar com o servidor de chamadas';
      sharedState.isConnecting = false;

      if (reconnectAttempts < maxReconnectAttempts) {
        const delay = Math.min(2000 * Math.pow(2, reconnectAttempts), 128000);
        reconnectAttempts++;
        reconnectTimer = setTimeout(() => {
          connect();
        }, delay);
      }
    }
  };

  const disconnect = (intentional = false) => {
    if (intentional) {
      isIntentionalDisconnect = true;
      reconnectAttempts = 0;
    }

    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }

    if (wsInstance) {
      wsInstance.close();
      wsInstance = null;
    }

    sharedState.isConnected = false;
    sharedState.isConnecting = false;
  };

  const sendMessage = message => {
    if (wsInstance && wsInstance.readyState === WebSocket.OPEN) {
      wsInstance.send(JSON.stringify(message));
    }
  };

  const handleMessage = data => {
    switch (data.type) {
      case 'auth_success':
        break;

      case 'connected':
        break;

      case 'call_incoming':
        handleIncomingCall(data.call);
        break;

      case 'call_answered': {
        clearAnswerTimeout();
        const currentUserId = store.getters.getCurrentUser?.id;
        const answeredByMe = !data.answeredBy || Number(data.answeredBy) === Number(currentUserId);

        if (answeredByMe) {
          // Este agente atendeu — atualizar o widget com estado "em chamada"
          sharedState.currentCall = {
            ...sharedState.currentCall,
            status: 'answered',
            answeredAt: data.answeredAt || data.timestamp,
            answered: true,
            conversationId: data.conversationId || sharedState.currentCall?.conversationId,
          };
          stopRingtone();
          saveCallState();
        } else {
          // Outro agente atendeu — limpar o widget deste agente
          console.log('[CallControl] 📵 Chamada atendida por outro agente, limpando widget');
          const callId = data.callId || data.channelId;
          const isCurrentCall = !callId || sharedState.currentCall?.id === callId;
          if (isCurrentCall) {
            sharedState.currentCall = null;
            stopRingtone();
            sharedState.isMuted = false;
            sharedState.isOnHold = false;
          }
          const idx = sharedState.activeCalls.findIndex(c => c.id === callId);
          if (idx !== -1) sharedState.activeCalls.splice(idx, 1);
          clearCallState();
        }
        break;
      }

      case 'call_ended':
        clearAnswerTimeout();
        if (sipHangupFn) { try { sipHangupFn(); } catch (e) {} }
        // Se for stale (chamada obsoleta restaurada do localStorage), garantir limpeza total
        if (data.reason === 'stale') { clearCallState(); sharedState.currentCall = null; sharedState.activeCalls = []; }

        if (sharedState.currentCall) {
          sharedState.currentCall = null;
          stopRingtone();
          sharedState.isMuted = false;
          sharedState.isOnHold = false;
        }

        // eslint-disable-next-line no-case-declarations
        const callIndex = sharedState.activeCalls.findIndex(c => c.id === data.callId);
        if (callIndex !== -1) {
          sharedState.activeCalls.splice(callIndex, 1);
        }

        if (sharedState.activeCalls.length > 0) {
          const nextCall = sharedState.activeCalls[0];
          sharedState.currentCall = nextCall;
          console.log('[CallControl] 📞 Chamada seguinte na fila:', nextCall.id);
          const ringtonePath = getRingtoneForCall(nextCall);
          playRingtone(ringtonePath);
        }

        clearCallState();
        break;

      case 'sip_bye':
        clearAnswerTimeout();
        if (sharedState.currentCall) {
          sharedState.currentCall = null;
          stopRingtone();
          sharedState.isMuted = false;
          sharedState.isOnHold = false;
        }

        if (data.callId) {
          const idx = sharedState.activeCalls.findIndex(c => c.id === data.callId);
          if (idx !== -1) {
            sharedState.activeCalls.splice(idx, 1);
          }
        }

        if (sharedState.activeCalls.length > 0) {
          const nextCall = sharedState.activeCalls[0];
          sharedState.currentCall = nextCall;
          console.log('[CallControl] 📞 Chamada seguinte na fila:', nextCall.id);
          const ringtonePath = getRingtoneForCall(nextCall);
          playRingtone(ringtonePath);
        }

        clearCallState();
        break;

      case 'call_missed':
        clearAnswerTimeout();
        sharedState.activeCalls = sharedState.activeCalls.filter(c => c.id !== data.channelId);
        if (sharedState.currentCall?.id === data.channelId) {
          sharedState.currentCall = null;
          stopRingtone();
        }
        clearCallState();
        break;

      case 'call_held':
        sharedState.isOnHold = true;
        break;

      case 'call_unheld':
        sharedState.isOnHold = false;
        break;

      case 'mute_toggled':
        sharedState.isMuted = data.isMuted;
        break;

      case 'mute_error':
        sharedState.isMuted = false;
        sharedState.error = data.error || 'Erro ao mutar chamada';
        break;

      case 'pong':
        // Heartbeat respondido pelo bridge — conexão ativa
        break;

      case 'error':
        sharedState.error = data.message;
        break;

      case 'call_queued':
        console.log('[CallControl] 📋 Chamada na fila:', data);
        break;

      case 'queue_updated':
        console.log('[CallControl] 📋 Fila atualizada:', data);
        sharedState.inQueue = data.data?.inQueue || 0;
        break;

      case 'agent_available':
        console.log('[CallControl] 👤 Agente disponível:', data.agentId);
        break;

      default:
        break;
    }
  };

  const handleIncomingCall = call => {
    const sipExtension = call.sipExtension;
    if (sipExtension) {
      sharedState.currentSipExtension = sipExtension;
    }

    const callInfo = {
      id: call.id || call.callId,
      callerNumber: call.callerNumber,
      callerName: call.callerName || 'Desconhecido',
      didNumber: call.didNumber,
      inboxId: call.inboxId,
      conversationId: call.conversationId,
      inboxName: call.inboxName || 'Telefone',
      startTime: call.timestamp || new Date().toISOString(),
      status: 'incoming',
      answered: false,
      sipExtension: sipExtension,
    };

    const existingCall = sharedState.activeCalls.find(c => c.id === callInfo.id);
    if (!existingCall) {
      sharedState.activeCalls.push(callInfo);

      if (!sharedState.currentCall || !sharedState.currentCall.answered) {
        sharedState.currentCall = callInfo;
      }

      saveCallState();
    }
  };

  const getRingtoneForCall = (call) => {
    if (!call) return '/audio/dashboard/ringtone.mp3';

    // 1) Toque cadastrado no Super Admin (Voip numbers), pelo inboxId da chamada
    const unit = getVoipUnitByInboxId(call.inboxId);
    if (unit?.ringtone_url) return unit.ringtone_url;

    // 2) Fallback: mapeamento por nome (mp3 fixos em public/audio/dashboard)
    const inboxName = call.inboxName || call.unit || '';
    const nameLower = inboxName.toLowerCase();

    if (nameLower.includes('andar de cima') || nameLower.includes('andar-de-cima')) {
      return '/audio/dashboard/ringtone_adc.mp3';
    } else if (nameLower.includes('lush')) {
      return '/audio/dashboard/ringtone_lush.mp3';
    } else if (nameLower.includes('tout')) {
      return '/audio/dashboard/ringtone_tout.mp3';
    } else if (nameLower.includes('altana')) {
      return '/audio/dashboard/ringtone_altana.mp3';
    }

    return '/audio/dashboard/ringtone.mp3';
  };

  const playRingtone = (ringtonePath = null) => {
    if (!ringtoneAudio) {
      const audioPath = ringtonePath || '/audio/dashboard/ringtone.mp3';
      ringtoneAudio = new Audio(audioPath);
      ringtoneAudio.loop = true;
    }
    ringtoneAudio.play().catch(err => {
      console.error('[CallControl] Erro ao reproduzir ringtone:', err);
    });
  };

  const stopRingtone = () => {
    if (ringtoneAudio) {
      ringtoneAudio.pause();
      ringtoneAudio.currentTime = 0;
      ringtoneAudio = null;
    }
  };

  const answerCall = callId => {
    const call = callId
      ? sharedState.activeCalls.find(c => c.id === callId)
      : sharedState.currentCall;

    if (!call) return;

    stopRingtone();
    sharedState.isAnswering = true;

    sendMessage({
      action: 'answer',
      channelId: call.id,
      userId: store.getters.getCurrentUser.id,
    });

    // Timeout de segurança: se o bridge não devolver call_answered nem call_ended
    // em 15s (ex.: INVITE SIP rejeitado com o browser em background — falha silenciosa),
    // limpa o widget e libera o agente para tentar de novo, em vez de travar.
    if (answerTimeout) clearTimeout(answerTimeout);
    answerTimeout = setTimeout(() => {
      answerTimeout = null;
      sharedState.isAnswering = false;
      const idx = sharedState.activeCalls.findIndex(c => c.id === call.id);
      if (idx !== -1) sharedState.activeCalls.splice(idx, 1);
      if (sharedState.currentCall?.id === call.id) sharedState.currentCall = null;
      clearCallState();
    }, 15000);
  };

  const hangupCall = callId => {
    // Busca a chamada em activeCalls primeiro. Se não encontrar (ex: já foi respondida
    // e saiu da lista), usa currentCall como fallback para não silenciar o desligamento.
    const call = callId
      ? (sharedState.activeCalls.find(c => c.id === callId) || sharedState.currentCall)
      : sharedState.currentCall;

    if (!call) return;

    stopRingtone();

    sendMessage({
      action: 'hangup',
      channelId: call.id,
    });
  };

  const toggleMute = () => {
    if (!sharedState.currentCall) return;

    sendMessage({
      type: 'toggle_mute',
      callId: sharedState.currentCall.id,
    });
  };

  const toggleHold = () => {
    if (!sharedState.currentCall) return;

    sendMessage({
      type: 'toggle_hold',
      callId: sharedState.currentCall.id,
    });
  };

  const transferCall = (destination, type = 'blind') => {
    if (!sharedState.currentCall) return;

    sendMessage({
      type: 'transfer_call',
      callId: sharedState.currentCall.id,
      destination,
      transferType: type,
    });
  };

  const updateCallControlStatus = (enabled) => {
    if (!isConnected.value) {
      return;
    }

    sendMessage({
      type: 'update_call_control_status',
      enabled,
    });
  };

  const registerSipHangup = (hangupFn) => {
    sipHangupFn = hangupFn;
  };

  const hangupSip = async () => {
    if (sipHangupFn && typeof sipHangupFn === 'function') {
      try {
        await sipHangupFn();
      } catch (err) {
        console.error('[CallControl] Erro ao desligar SIP:', err);
      }
    }
  };

  const playRingtoneOnIncoming = () => {
    if (sharedState.currentCall?.answered) return;

    const incomingCall = sharedState.activeCalls.find(c => c.status === 'incoming');
    if (incomingCall) {
      const ringtonePath = getRingtoneForCall(incomingCall);
      playRingtone(ringtonePath);
    } else {
      stopRingtone();
    }
  };

  const unwatch = watch(
    () => sharedState.activeCalls,
    () => {
      playRingtoneOnIncoming();
    },
    { deep: true }
  );

  const unwatchAvailability = watch(
    () => store.getters.getCurrentUserAvailability,
    (newAvailability, oldAvailability) => {
      if (newAvailability === oldAvailability) {
        return;
      }

      if (newAvailability === 'online' && oldAvailability !== 'online') {
        disconnect(true);
        setTimeout(() => {
          if (!isConnected.value) {
            connect();
          }
        }, 100);
      }

      if (newAvailability !== 'online' && oldAvailability === 'online') {
        disconnect(true);
      }
    }
  );

  const hasActiveCall = computed(() => !!sharedState.currentCall);
  const hasIncomingCalls = computed(
    () => sharedState.activeCalls.some(c => c.status === 'incoming')
  );
  const unreadCallCount = computed(
    () => sharedState.activeCalls.filter(c => c.status === 'incoming').length
  );

  onMounted(() => {
    componentCount++;
    const userAvailability = store.getters.getCurrentUserAvailability;

    // Registrar listeners de rede e visibilidade apenas uma vez (padrão singleton)
    if (componentCount === 1) {
      networkOnlineHandler = () => {
        console.log('[CallControl] 🌐 Rede restaurada, reconectando...');
        if (!sharedState.isConnected && !isIntentionalDisconnect) {
          const availability = store.getters.getCurrentUserAvailability;
          if (availability === 'online') {
            reconnectAttempts = 0;
            connect();
          }
        }
      };
      networkOfflineHandler = () => {
        console.log('[CallControl] 🌐 Rede perdida, suspendendo reconexões');
        if (reconnectTimer) {
          clearTimeout(reconnectTimer);
          reconnectTimer = null;
        }
      };
      // Quando a aba volta ao primeiro plano: reconecta imediatamente se necessário,
      // sem esperar o timer de backoff expirar (que pode estar congelado pelo browser).
      visibilityChangeHandler = () => {
        if (!document.hidden && !sharedState.isConnected && !isIntentionalDisconnect) {
          const availability = store.getters.getCurrentUserAvailability;
          if (availability === 'online') {
            console.log('[CallControl] 👁️ Aba visível, reconectando WebSocket...');
            if (reconnectTimer) {
              clearTimeout(reconnectTimer);
              reconnectTimer = null;
            }
            reconnectAttempts = 0;
            connect();
          }
        }
      };
      window.addEventListener('online', networkOnlineHandler);
      window.addEventListener('offline', networkOfflineHandler);
      document.addEventListener('visibilitychange', visibilityChangeHandler);
    }

    if (userAvailability === 'online') {
      connect();
    }
  });

  onUnmounted(() => {
    componentCount--;

    if (componentCount <= 0) {
      disconnect(true);
      stopRingtone();
      if (unwatch) unwatch();
      if (unwatchAvailability) unwatchAvailability();
      componentCount = 0;

      if (networkOnlineHandler) {
        window.removeEventListener('online', networkOnlineHandler);
        networkOnlineHandler = null;
      }
      if (networkOfflineHandler) {
        window.removeEventListener('offline', networkOfflineHandler);
        networkOfflineHandler = null;
      }
      if (visibilityChangeHandler) {
        document.removeEventListener('visibilitychange', visibilityChangeHandler);
        visibilityChangeHandler = null;
      }
    }
  });

  return {
    // Estado
    isConnected,
    isConnecting,
    currentCall,
    isMuted,
    isOnHold,
    activeCalls,
    error,
    hasActiveCall,
    hasIncomingCalls,
    unreadCallCount,
    isAnswering,

    // Estatísticas
    totalCallsToday,
    totalDurationToday,
    missedCallsToday,
    inQueue,

    currentSipExtension: computed(() => sharedState.currentSipExtension),

    // Métodos
    connect,
    disconnect,
    answerCall,
    hangupCall,
    toggleMute,
    toggleHold,
    transferCall,
    updateCallControlStatus,
    playRingtone,
    stopRingtone,

    // Integração SIP
    registerSipHangup,
    hangupSip,
  };
}
